import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../../api/axios";
import LegalPageShell from "./LegalPageShell";

const FALLBACK_COPY = {
  en: { loading: "Loading the current legal document…", error: "The current legal document could not be loaded.", retry: "Try again", version: "Version", effective: "Effective" },
  ru: { loading: "Загружаем актуальный юридический документ…", error: "Не удалось загрузить актуальный юридический документ.", retry: "Повторить", version: "Версия", effective: "Действует с" },
  sk: { loading: "Načítava sa aktuálny právny dokument…", error: "Aktuálny právny dokument sa nepodarilo načítať.", retry: "Skúsiť znova", version: "Verzia", effective: "Účinné od" },
};

const normalizedLanguage = (value) => {
  const code = String(value || "").toLowerCase().split("-")[0];
  return ["en", "ru", "sk"].includes(code) ? code : "en";
};

const formatEffectiveDate = (value, language) => {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat(language, { dateStyle: "long" }).format(parsed);
};

export default function VersionedLegalDocument({ documentType }) {
  const { i18n } = useTranslation();
  const language = normalizedLanguage(i18n.language);
  const copy = FALLBACK_COPY[language] || FALLBACK_COPY.en;
  const [bundle, setBundle] = useState(null);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let active = true;
    setError("");
    api
      .get("/accounts/legal/documents/", {
        params: { language },
        authOptional: true,
        suppressUnauthorizedRedirect: true,
        suppressGlobalError: true,
      })
      .then((response) => {
        if (active) setBundle(response.data || null);
      })
      .catch(() => {
        if (active) setError(copy.error);
      });
    return () => {
      active = false;
    };
  }, [copy.error, language, reloadKey]);

  const documentTypes = useMemo(
    () => (Array.isArray(documentType) ? documentType : [documentType]),
    [documentType]
  );
  const document = useMemo(
    () => bundle?.documents?.find((item) => documentTypes.includes(item.document_type)) || null,
    [bundle, documentTypes]
  );

  return (
    <LegalPageShell>
      {!bundle && !error ? <p className="py-16 text-center text-zinc-300">{copy.loading}</p> : null}
      {error ? (
        <div className="py-16 text-center">
          <p className="text-red-200">{error}</p>
          <button
            type="button"
            onClick={() => setReloadKey((value) => value + 1)}
            className="mt-5 rounded-full bg-[#3F73FF] px-5 py-3 font-semibold text-white"
          >
            {copy.retry}
          </button>
        </div>
      ) : null}
      {bundle && !document ? <p className="py-16 text-center text-red-200">{copy.error}</p> : null}
      {document ? (
        <>
          <h1 className="mb-3 text-center text-2xl font-bold md:text-3xl">{document.title}</h1>
          <p className="mb-8 text-center text-xs text-zinc-400 md:text-sm">
            {copy.version}: {document.version} · {copy.effective}: {formatEffectiveDate(document.effective_at, language)}
          </p>
          {(document.content || []).map((section, sectionIndex) => (
            <section key={`${section.heading || "intro"}-${sectionIndex}`} className="mb-7">
              {section.heading ? <h2 className="mb-3 mt-8 text-xl font-semibold">{section.heading}</h2> : null}
              {(section.paragraphs || []).map((paragraph, paragraphIndex) => (
                <p key={paragraphIndex} className="mb-4 whitespace-pre-line">{paragraph}</p>
              ))}
              {(section.bullets || []).length ? (
                <ul className="mb-4 list-disc space-y-2 pl-5">
                  {section.bullets.map((item, itemIndex) => <li key={itemIndex}>{item}</li>)}
                </ul>
              ) : null}
            </section>
          ))}
          {bundle?.contour === "RU" ? (
            <aside
              className="mt-10 rounded-2xl border border-white/15 bg-white/[0.06] p-5 text-sm leading-relaxed text-white/85"
              data-testid="rf-legal-contact"
            >
              <h2 className="font-semibold text-white">Контакты российского сервиса</h2>
              <p className="mt-2">
                ООО «ВОЛГРИД ВОСТОК» · телефон:{" "}
                <a className="font-semibold text-white underline underline-offset-2" href="tel:+79292182516">
                  +7 (929) 218-25-16
                </a>
              </p>
            </aside>
          ) : null}
        </>
      ) : null}
    </LegalPageShell>
  );
}
